import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormField } from "@/components/FormBits";
import { useAuth } from "@/context/AuthContext";
import { formatError, maskCpf } from "@/lib/api";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/conta";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", cpf: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : location.state?.from || "/conta"} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const run = (fn) => async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await fn();
      navigate(u.role === "admin" ? "/admin" : location.state?.from || "/conta", { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitBtn = (label, testId) => (
    <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-slate-900 hover:bg-[#0099DD]" data-testid={testId}>{busy ? "Aguarde..." : label}</Button>
  );

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Acesse sua conta</h1>
      <p className="mb-8 text-slate-500">Acompanhe seus pedidos em um só lugar.</p>
      <div className="space-y-6 rounded-md border border-slate-200 bg-white p-6">
        <Button type="button" variant="outline" onClick={googleLogin} className="h-11 w-full rounded-full font-semibold" data-testid="btn-google-login">
          <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continuar com Google
        </Button>
        <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou<span className="h-px flex-1 bg-slate-200" /></div>
        <Tabs defaultValue="login">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Criar conta</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={run(() => login(form.email, form.password))} className="space-y-4 pt-4" data-testid="login-form">
              <FormField id="login-email" label="E-mail" type="email" required value={form.email} onChange={set("email")} />
              <FormField id="login-password" label="Senha" type="password" required value={form.password} onChange={set("password")} />
              <div className="-mt-2 text-right">
                <Link to="/recuperar-senha" className="text-sm font-semibold text-[#0099DD] hover:underline" data-testid="link-forgot-password">Esqueci minha senha</Link>
              </div>
              {submitBtn("Entrar", "login-submit")}
            </form>
          </TabsContent>
          <TabsContent value="register">
            <form onSubmit={run(() => register(form))} className="space-y-4 pt-4" data-testid="register-form">
              <FormField id="register-name" label="Nome" required value={form.name} onChange={set("name")} />
              <FormField id="register-email" label="E-mail" type="email" required value={form.email} onChange={set("email")} />
              <FormField id="register-phone" label="Telefone / WhatsApp (com DDD)" type="tel" required placeholder="(11) 99999-9999" value={form.phone} onChange={set("phone")} />
              <FormField id="register-cpf" label="CPF" inputMode="numeric" required placeholder="000.000.000-00" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })} />
              <FormField id="register-password" label="Senha (mín. 6 caracteres)" type="password" minLength={6} required value={form.password} onChange={set("password")} />
              {submitBtn("Criar conta", "register-submit")}
            </form>
          </TabsContent>
        </Tabs>
        {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="auth-error">{error}</p>}
      </div>
    </div>
  );
}
